import { OfferSettingsGroup, ProjectOfferSettings, PropertyOfferSettings } from '../../../api/offers/types'
import { propertyResponseFields } from '../../../common/response/property'
import { normalizeDecimalFields } from '../../../utils/general'
import { renderAreaUnit, renderCurrency } from '../../../utils/property'
import { OfferModalItem, OfferModalProject, OfferModalProperty } from './types'

const currentLang = localStorage.getItem('i18nextLng') || 'hr'

// Type guards to check if item is project or property type
export const isProject = (item: OfferModalItem): item is OfferModalProject => {
    return 'projectType' in item
}

export const isProperty = (item: OfferModalItem): item is OfferModalProperty => {
    return 'propertyType' in item
}

/**
 * @param item - Property or project item
 * @param areaUnit - Area unit for display
 * @param currency - Currency for display
 * @returns Formatted display string
 */
export const formatItemDisplayLabel = (item: OfferModalItem, areaUnit: string, currency: string): string => {
    if (isProject(item)) {
        return `${item.internalAppId ? `${item.internalAppId}, ` : ''}${item.translations?.[currentLang].projectTitle || ''}`
    }

    if (isProperty(item)) {
        return `${item.internalAppId}, ${item.propertyService.name}, ${item.propertyType.name} | ${item.city.name}, ${item.widerArea.name} | ${item.area} ${renderAreaUnit(areaUnit)}, ${item.price.toLocaleString('hr-HR')} ${renderCurrency(currency, false)}`
    }

    return ''
}

/**
 * @param currentOfferSettingsGroup - Existing settings from formik (preserves user changes)
 * @param formattedItems - Items with photos attached (properties or projects)
 * @returns Array of offer settings for each item
 */
export const generateOfferSettingsGroup = (
    currentOfferSettingsGroup: OfferSettingsGroup[] = [],
    formattedItems: OfferModalItem[]
): OfferSettingsGroup[] => {
    return formattedItems.map((item): OfferSettingsGroup => {
        if (isProject(item)) {
            return {
                project_id: item.id,
            } as ProjectOfferSettings
        }

        // Properties - default case
        const currentSettings = currentOfferSettingsGroup?.find((settings) => 'property_id' in settings && settings.property_id === item.id) as
            | PropertyOfferSettings
            | undefined

        const normalizedFields = normalizeDecimalFields(item, [
            propertyResponseFields.PRICE,
            propertyResponseFields.PRICE_M2,
            propertyResponseFields.AREA,
        ]) as Record<string, string>

        const isPriceOnRequest = 'priceOnRequest' in item && item.priceOnRequest === 1

        return {
            property_id: item.id,
            offer_property_price:
                currentSettings?.offer_property_price !== undefined
                    ? currentSettings.offer_property_price
                    : isPriceOnRequest
                      ? null
                      : normalizedFields[propertyResponseFields.PRICE],
            offer_property_area:
                currentSettings?.offer_property_area !== undefined
                    ? currentSettings.offer_property_area
                    : normalizedFields[propertyResponseFields.AREA],
            offer_include_ground_plan: currentSettings?.offer_include_ground_plan ?? false,
            offer_include_watermark: currentSettings?.offer_include_watermark ?? false,
            offer_include_map: currentSettings?.offer_include_map ?? false,
            offer_exclude_agent: currentSettings?.offer_exclude_agent ?? false,
            offer_exclude_header: currentSettings?.offer_exclude_header ?? false,
            offer_photo_order: currentSettings?.offer_photo_order ?? item.photos?.map((photo) => photo.id).join(',') ?? '',
        } as PropertyOfferSettings
    })
}
